/**
 * `EmailLog` — page admin d'audit complet des emails partis.
 *
 * Après l'incident 2026-07-23 (fuite d'emails preview vers de vrais
 * candidats Layan), cette page devient LE point de traçabilité :
 * chaque appel à `send_email` — vrai envoi SMTP, dry-run pytest,
 * redirection safelist preview, drop dedupe, erreur SMTP — est
 * enregistré dans `email_log` et affiché ici.
 *
 * Filtres : recherche texte (destinataire / sujet), environnement,
 * fournisseur. Newest-first par défaut. Ouvre le HTML original en
 * clic sur une ligne.
 */
import { AlertTriangle, ArrowRight, CheckCircle2, Eye, Loader2, Mail, Search, ShieldAlert } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { api } from "../lib/api";


const ENV_OPTIONS = [
    { value: "", label: "Tous les environnements" },
    { value: "preview", label: "Preview (dev)" },
    { value: "prod", label: "Production" },
];

const PROVIDER_OPTIONS = [
    { value: "", label: "Tous les fournisseurs" },
    { value: "resend", label: "Resend (API)" },
    { value: "infomaniak-smtp", label: "Fallback SMTP (Infomaniak)" },
    { value: "dry-run", label: "Dry-run (pytest)" },
    { value: "dedupe", label: "Dédupliqué" },
    { value: "mock", label: "Mock" },
    { value: "smtp-error", label: "Erreur envoi" },
];

const PROVIDER_STYLES = {
    "resend": "bg-emerald-100 text-emerald-900",
    "infomaniak-smtp": "bg-emerald-100 text-emerald-900",
    "dry-run": "bg-slate-100 text-slate-700",
    "dedupe": "bg-amber-100 text-amber-900",
    "mock": "bg-slate-200 text-slate-700",
    "smtp-error": "bg-red-100 text-red-800",
};


export default function EmailLog() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [env, setEnv] = useState("");
    const [provider, setProvider] = useState("");
    const [selected, setSelected] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            params.set("limit", "500");
            if (q.trim()) params.set("q", q.trim());
            if (env) params.set("environment", env);
            if (provider) params.set("provider", provider);
            const { data } = await api.get(`/emails/log?${params.toString()}`);
            setRows(Array.isArray(data) ? data : []);
        } catch (e) {
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [q, env, provider]);

    useEffect(() => {
        load();
    }, [load]);

    const stats = useMemo(() => {
        const sent = rows.filter((r) => ["resend", "infomaniak-smtp"].includes(r.provider)).length;
        const redirected = rows.filter((r) => r.redirected).length;
        const errors = rows.filter((r) => r.provider === "smtp-error").length;
        return { sent, redirected, errors, total: rows.length };
    }, [rows]);

    return (
        <div className="p-6 max-w-7xl mx-auto space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                <div>
                    <h1 className="text-3xl font-black text-shariti-black flex items-center gap-2">
                        <Mail className="h-7 w-7 text-shariti-orange" />
                        Historique des emails
                    </h1>
                    <p className="text-sm text-slate-500 mt-1">
                        Traçabilité complète de chaque email envoyé depuis PREREC —
                        vrais envois, redirections preview, dry-runs, erreurs.
                    </p>
                </div>
                <div className="flex items-center gap-3 text-xs">
                    <div className="rounded-2xl bg-emerald-50 border-2 border-emerald-200 px-3 py-2 text-emerald-900 font-black flex items-center gap-1.5" data-testid="email-log-stats-sent">
                        <CheckCircle2 className="h-4 w-4" />
                        {stats.sent} envoyés
                    </div>
                    <div className="rounded-2xl bg-amber-50 border-2 border-amber-200 px-3 py-2 text-amber-900 font-black flex items-center gap-1.5" data-testid="email-log-stats-redirected">
                        <ShieldAlert className="h-4 w-4" />
                        {stats.redirected} redirigés
                    </div>
                    <div className="rounded-2xl bg-red-50 border-2 border-red-200 px-3 py-2 text-red-800 font-black flex items-center gap-1.5" data-testid="email-log-stats-errors">
                        <AlertTriangle className="h-4 w-4" />
                        {stats.errors} erreurs
                    </div>
                </div>
            </div>

            {/* Filtres */}
            <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <input
                        type="text"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Recherche par email ou sujet…"
                        className="w-full rounded-full border-2 border-slate-200 pl-9 pr-4 py-2 text-sm focus:border-shariti-orange focus:outline-none"
                        data-testid="email-log-search"
                    />
                </div>
                <select value={env} onChange={(e) => setEnv(e.target.value)} className="rounded-full border-2 border-slate-200 px-3 py-2 text-sm" data-testid="email-log-filter-env">
                    {ENV_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <select value={provider} onChange={(e) => setProvider(e.target.value)} className="rounded-full border-2 border-slate-200 px-3 py-2 text-sm" data-testid="email-log-filter-provider">
                    {PROVIDER_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <button
                    type="button"
                    onClick={load}
                    className="rounded-full border-2 border-slate-200 hover:border-shariti-orange px-4 py-2 text-xs font-black uppercase tracking-widest text-slate-700 hover:text-shariti-orange transition"
                    data-testid="email-log-refresh"
                >
                    Actualiser
                </button>
            </div>

            {/* Tableau */}
            <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
                {loading ? (
                    <div className="p-12 flex items-center justify-center text-sm text-slate-500 gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" /> Chargement…
                    </div>
                ) : rows.length === 0 ? (
                    <div className="p-12 text-center text-sm text-slate-500">
                        Aucun email dans l&apos;historique pour ces filtres.
                    </div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-[10px] font-black uppercase tracking-widest text-slate-500">
                            <tr>
                                <th className="text-left px-3 py-2">Date</th>
                                <th className="text-left px-3 py-2">Env.</th>
                                <th className="text-left px-3 py-2">Fournisseur</th>
                                <th className="text-left px-3 py-2">Destinataire</th>
                                <th className="text-left px-3 py-2">Sujet</th>
                                <th className="text-left px-3 py-2">Ouvert</th>
                                <th className="w-8"></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {rows.map((row) => (
                                <tr
                                    key={row.id}
                                    onClick={() => setSelected(row)}
                                    className="hover:bg-shariti-cream/50 cursor-pointer transition"
                                    data-testid={`email-log-row-${row.id}`}
                                >
                                    <td className="px-3 py-2 text-[11px] text-slate-600 whitespace-nowrap font-mono">
                                        {(row.sent_at || "").slice(0, 19).replace("T", " ")}
                                    </td>
                                    <td className="px-3 py-2">
                                        <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-black tracking-wider ${row.environment === "prod" ? "bg-emerald-100 text-emerald-900" : "bg-slate-100 text-slate-700"}`}>
                                            {row.environment || "?"}
                                        </span>
                                    </td>
                                    <td className="px-3 py-2">
                                        <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-black tracking-wider ${PROVIDER_STYLES[row.provider] || "bg-slate-100 text-slate-700"}`}>
                                            {row.provider}
                                        </span>
                                    </td>
                                    <td className="px-3 py-2 font-mono text-xs">
                                        {row.redirected ? (
                                            <span className="inline-flex items-center gap-1 text-amber-900">
                                                <span className="line-through text-slate-400">{row.original_to}</span>
                                                <ArrowRight className="h-3 w-3" />
                                                <strong>{row.to}</strong>
                                            </span>
                                        ) : (
                                            <span>{row.to}</span>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 truncate max-w-[400px] text-slate-700">{row.subject}</td>
                                    <td className="px-3 py-2">
                                        {row.opened_at ? (
                                            <span
                                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-sky-100 text-sky-900"
                                                title={`Ouvert le ${(row.opened_at || "").slice(0, 19).replace("T", " à ")}`}
                                                data-testid={`email-log-opened-${row.id}`}
                                            >
                                                <Eye className="h-3 w-3" />
                                                {row.open_count > 1 ? `×${row.open_count}` : "1"}
                                            </span>
                                        ) : ["resend", "infomaniak-smtp"].includes(row.provider) ? (
                                            <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">
                                                —
                                            </span>
                                        ) : (
                                            <span className="text-[10px] text-slate-300">n/a</span>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 text-slate-400">›</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Modal preview */}
            {selected && (
                <EmailPreviewModal
                    entry={selected}
                    onClose={() => setSelected(null)}
                />
            )}
        </div>
    );
}


function EmailPreviewModal({ entry, onClose }) {
    const [full, setFull] = useState(null);

    useEffect(() => {
        api.get(`/emails/log/${entry.id}`).then(({ data }) => setFull(data));
    }, [entry.id]);

    return (
        <div
            className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
            onClick={onClose}
            data-testid="email-log-preview-modal"
        >
            <div
                className="bg-white rounded-3xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="p-5 border-b border-slate-100 flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                        <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                            {entry.provider} · {entry.environment}
                        </div>
                        <div className="text-lg font-black text-shariti-black truncate mt-0.5">
                            {entry.subject}
                        </div>
                        <div className="text-xs text-slate-500 mt-1 font-mono">
                            Envoyé à <strong>{entry.to}</strong>
                            {entry.redirected && entry.original_to !== entry.to && (
                                <span className="text-amber-700"> (redirigé depuis {entry.original_to})</span>
                            )}
                            {" · "}{(entry.sent_at || "").slice(0, 19).replace("T", " ")}
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-slate-400 hover:text-slate-700 text-2xl leading-none"
                        aria-label="Fermer"
                    >
                        ×
                    </button>
                </div>
                <div className="flex-1 overflow-y-auto p-5 bg-slate-50">
                    {full === null ? (
                        <div className="text-center text-sm text-slate-500 py-12">
                            <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Chargement…
                        </div>
                    ) : full.html_preview ? (
                        <div
                            className="bg-white rounded-2xl shadow p-1"
                            dangerouslySetInnerHTML={{ __html: full.html_preview }}
                        />
                    ) : (
                        <div className="text-sm text-slate-500 italic">Contenu HTML non disponible.</div>
                    )}
                    {full?.error && (
                        <div className="mt-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-3 font-mono">
                            <strong>Erreur SMTP :</strong> {full.error}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
